import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DatePicker } from 'antd';
import moment from 'moment';
import Swal from 'sweetalert2';
import { default as ReactSelect } from 'react-select';
import { Button, Col, Row } from 'reactstrap';
import { HandleSelect, Option, ValueContainer, multiSelectSearchStyles } from './MultiSelectControls';
import {
    SALES_DATE_MODES,
    getDefaultSalesListSettings,
    normalizeDisplayDate,
    sanitizeSalesListSettings,
} from '../utils/salesListSettings';

function buildSalesListSettingsPayload(settings, selectedPlant, selectedCustomer, allowedPlants, customers) {
    const areAllPlantsSelected = allowedPlants.every((plant) =>
        selectedPlant.some((selected) => selected.value === plant.value),
    );
    const areAllCustomersSelected = customers.every((customer) =>
        selectedCustomer.some((selected) => selected.value === customer.value),
    );

    const plantIds = areAllPlantsSelected
        ? []
        : selectedPlant
            .filter((plant) => plant.value !== 'select-all')
            .map((plant) => plant.value);
    const customerIds = areAllCustomersSelected
        ? []
        : selectedCustomer
            .filter((customer) => customer.value !== 'select-all')
            .map((customer) => customer.value);

    return {
        ...settings,
        plantIds,
        customerIds,
    };
}

function toPickerMoment(value) {
    if (!value) {
        return null;
    }
    if (moment.isMoment(value)) {
        return value.isValid() ? value : null;
    }
    const normalized = normalizeDisplayDate(value);
    if (!normalized) {
        return null;
    }
    const parsed = moment(normalized, 'DD-MM-YYYY', true);
    return parsed.isValid() ? parsed : null;
}

const datePickerPopupProps = {
    getPopupContainer: () => document.body,
    dropdownClassName: 'sales-settings-date-popup',
};

const SalesListSettingsForm = forwardRef(function SalesListSettingsForm({
    allowedPlants = [],
    customers = [],
    initialSettings,
    onSave,
    onCancel,
    showActions = true,
    showCloseButton = true,
    showSectionHeader = true,
    hideCustomers = false,
}, ref) {
    const [settings, setSettings] = useState(() => (
        sanitizeSalesListSettings(initialSettings || getDefaultSalesListSettings())
    ));
    const [selectedPlant, setSelectedPlant] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);

    const plantsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All plants' }, ...allowedPlants],
        [allowedPlants],
    );
    const customersWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All customers' }, ...customers],
        [customers],
    );

    const filterSelectOption = (option, inputValue) => {
        if (!inputValue) {
            return true;
        }
        if (option.value === 'select-all') {
            return 'select all'.includes(inputValue.toLowerCase());
        }
        return String(option.label || '').toLowerCase().includes(inputValue.toLowerCase());
    };

    const sharedSelectProps = {
        isMulti: true,
        isSearchable: true,
        controlShouldRenderValue: false,
        closeMenuOnSelect: false,
        hideSelectedOptions: false,
        openMenuOnFocus: true,
        openMenuOnClick: true,
        blurInputOnSelect: false,
        menuPlacement: 'auto',
        maxMenuHeight: 220,
        components: { Option, ValueContainer },
        styles: multiSelectSearchStyles,
        isClearable: true,
        filterOption: filterSelectOption,
    };

    const getCurrentSettings = () => buildSalesListSettingsPayload(
        settings,
        selectedPlant,
        selectedCustomer,
        allowedPlants,
        customers,
    );

    useImperativeHandle(ref, () => ({
        getSettings: () => sanitizeSalesListSettings(getCurrentSettings()),
    }));

    useEffect(() => {
        setSettings(sanitizeSalesListSettings(initialSettings || getDefaultSalesListSettings()));
    }, [initialSettings]);

    useEffect(() => {
        const source = sanitizeSalesListSettings(initialSettings || getDefaultSalesListSettings());

        const plantSelection = source.plantIds?.length
            ? allowedPlants.filter((plant) => source.plantIds.includes(plant.value))
            : plantsWithSelectAll.filter((plant) => plant.value !== 'select-all');
        setSelectedPlant(
            plantSelection.length === allowedPlants.length
                ? plantsWithSelectAll
                : plantSelection,
        );

        const customerSelection = source.customerIds?.length
            ? customers.filter((customer) => source.customerIds.includes(customer.value))
            : customersWithSelectAll.filter((customer) => customer.value !== 'select-all');
        setSelectedCustomer(
            customerSelection.length === customers.length
                ? customersWithSelectAll
                : customerSelection,
        );
    }, [initialSettings, allowedPlants, customers, plantsWithSelectAll, customersWithSelectAll]);

    const onPlantSelect = (selected) => {
        HandleSelect(selected, selectedPlant, setSelectedPlant, allowedPlants, plantsWithSelectAll);
    };

    const onCustomerSelect = (selected) => {
        HandleSelect(selected, selectedCustomer, setSelectedCustomer, customers, customersWithSelectAll);
    };

    const handleDateModeChange = (event) => {
        const dateMode = event.target.value;
        setSettings((current) => {
            if (dateMode !== SALES_DATE_MODES.DATE_RANGE) {
                return { ...current, dateMode };
            }

            const defaults = getDefaultSalesListSettings();
            return {
                ...current,
                dateMode,
                customFromDate: normalizeDisplayDate(current.customFromDate) || defaults.customFromDate,
                customToDate: normalizeDisplayDate(current.customToDate) || defaults.customToDate,
            };
        });
    };

    const handleFromDateChange = (date) => {
        const nextDate = toPickerMoment(date);
        if (!nextDate) {
            return;
        }
        setSettings((current) => ({
            ...current,
            dateMode: SALES_DATE_MODES.DATE_RANGE,
            customFromDate: nextDate.format('DD-MM-YYYY'),
        }));
    };

    const handleToDateChange = (date) => {
        const nextDate = toPickerMoment(date);
        if (!nextDate) {
            return;
        }
        setSettings((current) => ({
            ...current,
            dateMode: SALES_DATE_MODES.DATE_RANGE,
            customToDate: nextDate.format('DD-MM-YYYY'),
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const payload = getCurrentSettings();

        if (payload.dateMode === SALES_DATE_MODES.DATE_RANGE) {
            const from = normalizeDisplayDate(payload.customFromDate);
            const to = normalizeDisplayDate(payload.customToDate);
            if (!from || !to) {
                Swal.fire(
                    'Invalid date range',
                    'Please select valid From and To dates using the calendar.',
                    'warning',
                );
                return;
            }
        }

        onSave?.(sanitizeSalesListSettings(payload));
    };

    const defaults = getDefaultSalesListSettings();
    const customFrom = toPickerMoment(settings.customFromDate)
        || toPickerMoment(defaults.customFromDate);
    const customTo = toPickerMoment(settings.customToDate)
        || toPickerMoment(defaults.customToDate);

    const fields = (
        <div className="sales-settings-section">
            {showSectionHeader ? (
                <>
                    <h5 className="sales-settings-section-title">General Settings for Sales</h5>
                    <p className="sales-settings-section-subtitle">
                        Configure default plants, customers, and date range for sales list views.
                    </p>
                </>
            ) : null}

            <Row className="mb-3">
                <Col md={12}>
                    <label className="sales-settings-label">User allowed plants</label>
                    <ReactSelect
                        {...sharedSelectProps}
                        options={plantsWithSelectAll}
                        value={selectedPlant}
                        onChange={onPlantSelect}
                        className="react-select-dropdown"
                        placeholder="Please select Plant"
                    />
                </Col>
            </Row>

            {!hideCustomers ? (
                <Row className="mb-3">
                    <Col md={12}>
                        <label className="sales-settings-label">Customers</label>
                        <ReactSelect
                            {...sharedSelectProps}
                            options={customersWithSelectAll}
                            value={selectedCustomer}
                            onChange={onCustomerSelect}
                            className="react-select-dropdown"
                            placeholder="Please select Customer"
                        />
                    </Col>
                </Row>
            ) : null}

            <Row className="mb-3">
                <Col md={12}>
                    <label className="sales-settings-label">Date range</label>
                    <div className="sales-settings-date-options">
                        <label className="sales-settings-radio">
                            <input
                                type="radio"
                                name="salesDateMode"
                                value={SALES_DATE_MODES.CURRENT_MONTH}
                                checked={settings.dateMode === SALES_DATE_MODES.CURRENT_MONTH}
                                onChange={handleDateModeChange}
                            />
                            Current month (default)
                        </label>
                        <label className="sales-settings-radio">
                            <input
                                type="radio"
                                name="salesDateMode"
                                value={SALES_DATE_MODES.LAST_TWO_MONTHS}
                                checked={settings.dateMode === SALES_DATE_MODES.LAST_TWO_MONTHS}
                                onChange={handleDateModeChange}
                            />
                            Last two months
                        </label>
                        <label className="sales-settings-radio">
                            <input
                                type="radio"
                                name="salesDateMode"
                                value={SALES_DATE_MODES.DATE_RANGE}
                                checked={settings.dateMode === SALES_DATE_MODES.DATE_RANGE}
                                onChange={handleDateModeChange}
                            />
                            Date range
                        </label>
                    </div>
                    {settings.dateMode === SALES_DATE_MODES.DATE_RANGE ? (
                        <div className="sales-settings-date-range">
                            <div className="sales-settings-date-field">
                                <label className="sales-settings-date-field-label">From date</label>
                                <DatePicker
                                    {...datePickerPopupProps}
                                    value={customFrom}
                                    format="DD-MM-YYYY"
                                    onChange={handleFromDateChange}
                                    allowClear={false}
                                    placement="bottomLeft"
                                    style={{ height: '40px', width: '100%' }}
                                />
                            </div>
                            <div className="sales-settings-date-field">
                                <label className="sales-settings-date-field-label">To date</label>
                                <DatePicker
                                    {...datePickerPopupProps}
                                    value={customTo}
                                    format="DD-MM-YYYY"
                                    onChange={handleToDateChange}
                                    allowClear={false}
                                    placement="topLeft"
                                    style={{ height: '40px', width: '100%' }}
                                />
                            </div>
                        </div>
                    ) : null}
                </Col>
            </Row>
        </div>
    );

    if (!showActions) {
        return <div className="sales-settings-embedded">{fields}</div>;
    }

    return (
        <form className="form" onSubmit={handleSubmit}>
            {fields}
            <div className="footer text-center">
                <Button
                    type="submit"
                    style={{ width: '90px', fontWeight: 'bold', backgroundColor: 'RGB(42,245,39,0.8)' }}
                    className="btn btn-twitter"
                >
                    Update
                </Button>
                &nbsp;&nbsp;
                {showCloseButton ? (
                    <Button
                        type="button"
                        style={{ width: '90px', fontWeight: 'bold', backgroundColor: 'RGB(58,29,238,0.8)' }}
                        className="btn btn-twitter"
                        onClick={onCancel}
                    >
                        Close
                    </Button>
                ) : null}
            </div>
        </form>
    );
});

export default SalesListSettingsForm;
